import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Drawer, { DrawerPresentationProps, DrawerProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Menu from "@insite/mobius/Icons/Menu";
import User from "@insite/mobius/Icons/User";
import LazyImage from "@insite/mobius/LazyImage";
// import { MappedLink } from "@insite/mobius/Menu";
import PanelMenu, { PanelMenuPresentationProps } from "@insite/mobius/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const linkListStateLinks = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderLinkList";
    })?.fields.links;

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        // signOutUrl: getPageLinkByPageType(state, "SignOutPage"),
        headerLinkListLinks: mapLinks<LinkModel, { openInNewWindow: boolean }>(
            state,
            linkListStateLinks,
            widgetLink => ({
                openInNewWindow: widgetLink.fields.openInNewWindow,
            }),
        ),
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        drawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    setInitialValues,
    setNavDrawerIsOpen,
};

export interface MappedLink {
    title: string;
    url: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    categoryImage?: string;
}

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
}

interface MainNavigationDrawerState extends Pick<MainNavigationDrawerProps, "currentLocation"> {
    open?: boolean;
}

type MainNavigationDrawerProps = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    menuLabel?: InjectableCss;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        css: css`
            background: rgb(168, 16, 16);
            border: rgb(168, 16, 16);
            display: flex;
            align-items: center;
            &:hover {
                background: rgb(168, 16, 16);
                border: rgb(168, 16, 16);
            }
        `,
    },
    menuLabel: {
        css: css`
            color: #fff;
            font-weight: 600;
            text-transform: capitalize;
            font-size: 15px;
            padding: 3px 5px 0 5px;
        `,
    },
    drawer: {
        size: 300,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
            `,
            drawerTitle: css`
                background: #fff;
                color: #333 !important;
                h4 {
                    margin: 7px 0 7px 20px;
                    font-size: 21px;
                }
            `,
            titleButton: css`
                background: #fff;
                border: none;
                span[class^="IconWrapper"] {
                    color: #333 !important;
                }
                &:hover {
                    background: #fff;
                    border: none;
                }
            `,
            drawerBody: css`
                background: #fff;
            `,
            scrim: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            display: none;
                        `,
                        css`
                            ${window.pageYOffset > 123 ? "top: 50px" : "top: 0"}
                        `,
                        css`
                            ${window.pageYOffset > 123 ? "top: 50px" : "top: 0"}
                        `,
                    ])}
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                padding-bottom: 10px;
                background: #fff;
            }
            &:last-child {
                margin-bottom: 5px;
            }
            margin-top: 20px;
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                div[class^="PanelChildren"] {
                    z-index: 10;
                }
                > button {
                    width: calc(100% - 2px);
                    background: #fff;
                    span[class^="TypographyStyle"] {
                        color: #333 !important;
                    }
                    span[class^="IconWrapper"] {
                        color: #333 !important;
                        height: 22px;
                    }
                }
            `,
            children: css`
                div[class^="PanelRowStyle"] {
                    background: #fff;
                    > button {
                        background: #fff;
                        border: none;
                        > span[class^="IconWrapper"] {
                            color: #333 !important;
                        }
                    }
                }
                div[class^="PanelMenuStyle"] {
                    background: #fff;
                    a[class^="StyledButton"] {
                        background: #fff;
                        span[class^="TypographyStyle"] {
                            text-transform: capitalize;
                            color: #333 !important;
                        }
                    }
                }
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        transform: "capitalize",
        color: "#333",
    },
    mainNavigationRow: {
        color: "#fff",
        css: css`
            margin: 2px;
        `,
    },
    mainNavigationRowIcon: {
        color: "#fff",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    mainNavigationRowTypography: {
        transform: "capitalize",
        color: "#333",
        weight: "bold",
    },
    logoLinks: {
        color: "#333",
        typographyProps: {
            ellipsis: true,
            transform: "capitalize",
        },
        css: css`
            margin: 2px;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
};

const styles = navigationDrawerStyles;

class MainNavigationDrawer extends React.Component<MainNavigationDrawerProps, MainNavigationDrawerState> {
    constructor(props: MainNavigationDrawerProps) {
        super(props);
        this.state = {
            currentLocation: props.currentLocation,
        };
    }

    componentDidUpdate() {
        const headerElement = document.querySelector('[data-test-selector="secondaryNavigation"]');
        if (headerElement) {
            headerElement.addEventListener("click", () => {
                this.closeDrawer();
            });
        }
        const searchElement = document.querySelector('[data-test-selector="searchNav"]');
        if (searchElement) {
            searchElement.addEventListener("click", () => {
                this.closeDrawer();
            });
        }
    }

    openDrawer = () => {
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: true });
        this.props.setInitialValues({});
    };

    closeDrawer = () => {
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => this.props.setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    render() {
        const {
            quickOrderLink,
            links,
            showQuickOrder,
            currencies,
            currentCurrencyId,
            currentCurrencySymbol,
            languages,
            currentLanguage,
            setLanguage,
            setCurrency,
            userName,
            isGuest,
            myAccountPageLink,
            signInUrl,
            signOut,
            currentLocation,
            showCustomerMenuItem,
            fulfillmentLabel,
        } = this.props;

        const currentPageUrl = currentLocation.pathname;

        return (
            <>
                <Button onClick={this.openDrawer} {...styles.menuTriggerButton} data-test-selector="expandMobileMenu">
                    <ButtonIcon src={Menu} /> <Typography {...styles.menuLabel}>Shop By Category</Typography>
                </Button>
                <Drawer
                    draggable
                    position="left"
                    {...(styles.drawer as DrawerProps)}
                    isOpen={this.props.drawerIsOpen}
                    handleClose={this.closeDrawer}
                    contentLabel="menu drawer"
                    headline="Shop By Category"
                >
                    <StyledSection {...styles.drawerSectionWrapper}>
                        {links.map((link, index) => {
                            if (link.children && link.children.length > 0) {
                                return (
                                    <PanelMenu
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={this.closeDrawer}
                                        panelTrigger={
                                            <PanelRow hasChildren {...styles.mainNavigationRow}>
                                                <StyledWrapper
                                                    id="wrapper"
                                                    css={css`
                                                        display: flex;
                                                        align-items: center;
                                                    `}
                                                >
                                                    {link?.categoryImage && link?.childrenType === "CascadingMenu" && (
                                                        <LazyImage
                                                            src={link?.categoryImage}
                                                            css={css`
                                                                height: 50px;
                                                                width: 50px;
                                                                border: 1px solid #a81010;
                                                                margin-right: 10px;
                                                            `}
                                                        />
                                                    )}
                                                    <Typography {...styles.mainNavigationRowTypography}>
                                                        {link.title}
                                                    </Typography>
                                                </StyledWrapper>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    href={link.url}
                                    onClick={this.closeDrawer}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography {...styles.mainNavigationRowTypography}>{link.title}</Typography>
                                </PanelRow>
                            );
                        })}
                    </StyledSection>
                    {this.props.headerLinkListLinks.length > 0 && (
                        <StyledSection {...styles.drawerSectionWrapper}>
                            {this.props.headerLinkListLinks.map(link => (
                                <PanelRow
                                    key={link.title}
                                    {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                    isCurrent={currentPageUrl === link.url}
                                    onClick={this.closeDrawer}
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                >
                                    <Typography {...styles.logoLinks?.typographyProps}>{link.title}</Typography>
                                </PanelRow>
                            ))}
                        </StyledSection>
                    )}
                </Drawer>
            </>
        );
    }
}

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption: React.ReactNode;
    currentOptionIcon: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren {...styles.logoLinks}>
            <StyledSpan {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MainNavigationDrawer);
